import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { salesByCategoryService } from "../../utils/services/salesByCategoryService";
import { toast } from "react-toastify";
import CurrencyTextField from "../currency-field/CurrencyTextField";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { categoryService } from "../../utils/services/categoryService";
import { clientService } from "../../utils/services/clientService";

const SalesByCategoryModal = ({
  open,
  selectedEntry,
  handleClose,
  onAddData,
  clientId,
}) => {
  // For EDIT: single flat form
  const [editData, setEditData] = useState({
    dimension: "category",
    category: null,
    product_type: "",
    client: null,
    unit_sold: "",
    total_revenue: "",
    dropship_revenue: "",
    dropship_units: "",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  // For CREATE: period (incl. grouping dimension) is fixed at top, rows added below
  const [period, setPeriod] = useState({
    dimension: "category",
    client: null,
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [rowData, setRowData] = useState({
    category: null,
    product_type: "",
    unit_sold: "",
    total_revenue: "",
    dropship_revenue: "",
    dropship_units: "",
  });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedEntry && open) {
      setEditData({
        dimension:
          selectedEntry.dimension === "product_type"
            ? "product_type"
            : "category",
        category: selectedEntry.category || null,
        product_type: selectedEntry.product_type || "",
        client: selectedEntry.client || null,
        unit_sold: selectedEntry.unit_sold || "",
        total_revenue: selectedEntry.total_revenue || "",
        dropship_revenue: selectedEntry.dropship_revenue || "",
        dropship_units: selectedEntry.dropship_units || "",
        month: selectedEntry.month || new Date().getMonth() + 1,
        year: selectedEntry.year || new Date().getFullYear(),
      });
    } else if (!selectedEntry && open) {
      setPeriod({
        dimension: "category",
        client: null,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
      });
      setRowData({
        category: null,
        product_type: "",
        unit_sold: "",
        total_revenue: "",
        dropship_revenue: "",
        dropship_units: "",
      });
      setEntries([]);
    }
  }, [selectedEntry, open]);

  // --- Edit handlers ---
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: [
        "unit_sold",
        "total_revenue",
        "dropship_revenue",
        "dropship_units",
        "month",
        "year",
      ].includes(name)
        ? value === ""
          ? ""
          : parseFloat(value)
        : value,
    }));
  };

  // --- Create row handlers ---
  const handleRowChange = (e) => {
    const { name, value } = e.target;
    setRowData((prev) => ({
      ...prev,
      [name]: value === "" ? "" : parseFloat(value),
    }));
  };

  const handleAddRow = () => {
    if (period.dimension === "product_type") {
      if (!rowData.product_type?.trim()) {
        toast.error("Please enter a product type");
        return;
      }
    } else if (!rowData.category) {
      toast.error("Please select a category");
      return;
    }
    if (!rowData.unit_sold || rowData.unit_sold < 0) {
      toast.error("Please enter valid unit sold");
      return;
    }
    if (!rowData.total_revenue || rowData.total_revenue < 0) {
      toast.error("Please enter valid total revenue");
      return;
    }
    if (!period.month || period.month < 1 || period.month > 12) {
      toast.error("Month must be between 1 and 12");
      return;
    }
    if (!period.year || period.year < 2000 || period.year > 2100) {
      toast.error("Year must be between 2000 and 2100");
      return;
    }
    if (!clientId && !period.client) {
      toast.error("Please select a client");
      return;
    }

    setEntries((prev) => [
      ...prev,
      {
        id: Date.now(),
        category: rowData.category,
        product_type: rowData.product_type?.trim() || "",
        unit_sold: rowData.unit_sold,
        total_revenue: rowData.total_revenue,
        dropship_revenue: rowData.dropship_revenue || 0,
        dropship_units: rowData.dropship_units || 0,
      },
    ]);
    setRowData({
      category: null,
      product_type: "",
      unit_sold: "",
      total_revenue: "",
      dropship_revenue: "",
      dropship_units: "",
    });
  };

  const handleRemoveEntry = (id) =>
    setEntries((prev) => prev.filter((e) => e.id !== id));

  // --- Submit ---
  const handleSubmit = async () => {
    if (selectedEntry) {
      if (editData.dimension === "product_type") {
        if (!editData.product_type?.trim()) {
          toast.error("Please enter a product type");
          return;
        }
      } else if (!editData.category) {
        toast.error("Please select a category");
        return;
      }
      if (!clientId && !editData.client) {
        toast.error("Please select a client");
        return;
      }
      if (!editData.unit_sold || editData.unit_sold < 0) {
        toast.error("Please enter valid unit sold");
        return;
      }
      if (!editData.total_revenue || editData.total_revenue < 0) {
        toast.error("Please enter valid total revenue");
        return;
      }
      if (editData.month < 1 || editData.month > 12) {
        toast.error("Month must be between 1 and 12");
        return;
      }
      if (editData.year < 2000 || editData.year > 2100) {
        toast.error("Year must be between 2000 and 2100");
        return;
      }

      setLoading(true);
      try {
        const payload = {
          dimension: editData.dimension,
          ...(editData.dimension === "product_type"
            ? { product_type: editData.product_type.trim(), category: null }
            : { category: editData.category?._id || editData.category }),
          client: clientId || editData.client?._id || editData.client,
          unit_sold: editData.unit_sold,
          total_revenue: editData.total_revenue,
          dropship_revenue:
            editData.dropship_revenue === "" ? 0 : editData.dropship_revenue,
          dropship_units:
            editData.dropship_units === "" ? 0 : editData.dropship_units,
          month: editData.month,
          year: editData.year,
        };
        await salesByCategoryService.edit(payload, selectedEntry._id);
        toast.success("Sales entry updated successfully!");
        onAddData && onAddData();
        handleClose();
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to save sales entry",
        );
      } finally {
        setLoading(false);
      }
    } else {
      if (entries.length === 0) {
        toast.error("Please add at least one entry");
        return;
      }

      setLoading(true);
      try {
        const resolvedClient = clientId || period.client?._id || period.client;
        await salesByCategoryService.create(
          entries.map((entry) => ({
            dimension: period.dimension,
            ...(period.dimension === "product_type"
              ? { product_type: entry.product_type }
              : { category: entry.category._id }),
            client: resolvedClient,
            unit_sold: entry.unit_sold,
            total_revenue: entry.total_revenue,
            dropship_revenue: entry.dropship_revenue || 0,
            dropship_units: entry.dropship_units || 0,
            month: period.month,
            year: period.year,
          })),
        );
        toast.success(`${entries.length} sales entries created successfully!`);
        onAddData && onAddData();
        handleClose();
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to save sales entries",
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {selectedEntry ? "Edit Sales Entry" : "Add Sales Entries"}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {selectedEntry ? (
          /* ── EDIT MODE: single flat form ── */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Grouped By *</InputLabel>
              <Select
                value={editData.dimension}
                label="Grouped By *"
                onChange={(e) =>
                  setEditData((p) => ({
                    ...p,
                    dimension: e.target.value,
                    category: null,
                    product_type: "",
                  }))
                }
              >
                <MenuItem value="category">Category</MenuItem>
                <MenuItem value="product_type">Product Type</MenuItem>
              </Select>
            </FormControl>
            {!clientId && (
              <AutocompleteWithPagination
                customLabel="Client *"
                selectedItem={editData.client}
                onDataChange={(v) =>
                  setEditData((p) => ({ ...p, client: v || null }))
                }
                width="100%"
                optionKey="name"
                backgroundColor="var(--bg-paper)"
                apiEndPoint={(limit, skip, search) =>
                  salesByCategoryService.fetchClients(search, limit, skip)
                }
              />
            )}
            {editData.dimension === "product_type" ? (
              <TextField
                label="Product Type *"
                value={editData.product_type}
                onChange={(e) =>
                  setEditData((p) => ({ ...p, product_type: e.target.value }))
                }
                fullWidth
              />
            ) : (
              <AutocompleteWithPagination
                customLabel="Category *"
                selectedClient={clientId ? { _id: clientId } : editData.client}
                backgroundColor="var(--bg-paper)"
                selectedItem={editData.category}
                onDataChange={(v) =>
                  setEditData((p) => ({ ...p, category: v || null }))
                }
                width="100%"
                optionKey="name"
                apiEndPoint={(limit, skip, search) =>
                  categoryService.fetchNames(
                    search,
                    clientId || editData.client?._id,
                    limit,
                    skip,
                  )
                }
              />
            )}
            <TextField
              label="Unit Sold *"
              name="unit_sold"
              type="number"
              value={editData.unit_sold}
              onChange={handleEditChange}
              inputProps={{ step: "1", min: "0" }}
              fullWidth
            />
            <CurrencyTextField
              label="Total Revenue *"
              name="total_revenue"
              value={editData.total_revenue}
              onChange={(value) =>
                handleEditChange({ target: { name: "total_revenue", value } })
              }
              fullWidth
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <CurrencyTextField
                label="DropShip Revenue"
                name="dropship_revenue"
                value={editData.dropship_revenue}
                onChange={(value) =>
                  handleEditChange({
                    target: { name: "dropship_revenue", value },
                  })
                }
                fullWidth
              />
              <TextField
                label="DropShip Units"
                name="dropship_units"
                type="number"
                value={editData.dropship_units}
                onChange={handleEditChange}
                inputProps={{ step: "1", min: "0" }}
                fullWidth
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Month *"
                name="month"
                type="number"
                value={editData.month}
                onChange={handleEditChange}
                inputProps={{ min: "1", max: "12" }}
                fullWidth
              />
              <TextField
                label="Year *"
                name="year"
                type="number"
                value={editData.year}
                onChange={handleEditChange}
                inputProps={{ min: "2000", max: "2100" }}
                fullWidth
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              * Required fields
            </Typography>
          </Box>
        ) : (
          /* ── CREATE MODE: period first, then category rows ── */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Period header */}
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, bgcolor: "var(--bg-paper)" }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Period
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Grouped By *</InputLabel>
                <Select
                  value={period.dimension}
                  label="Grouped By *"
                  onChange={(e) => {
                    setEntries([]);
                    setRowData((p) => ({
                      ...p,
                      category: null,
                      product_type: "",
                    }));
                    setPeriod((p) => ({ ...p, dimension: e.target.value }));
                  }}
                >
                  <MenuItem value="category">Category</MenuItem>
                  <MenuItem value="product_type">Product Type</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Month *</InputLabel>
                  <Select
                    name="month"
                    value={period.month}
                    label="Month *"
                    onChange={(e) => {
                      setEntries([]);
                      setPeriod((p) => ({ ...p, month: e.target.value }));
                    }}
                  >
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((m, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Year *</InputLabel>
                  <Select
                    name="year"
                    value={period.year}
                    label="Year *"
                    onChange={(e) => {
                      setEntries([]);
                      setPeriod((p) => ({ ...p, year: e.target.value }));
                    }}
                  >
                    {Array.from(
                      { length: 20 },
                      (_, i) => new Date().getFullYear() - i,
                    ).map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {!clientId && (
                <Box sx={{ mt: 1.5 }}>
                  <AutocompleteWithPagination
                    customLabel="Client *"
                    selectedItem={period.client}
                    onDataChange={(v) => {
                      setEntries([]);
                      setPeriod((p) => ({ ...p, client: v || null }));
                    }}
                    width="100%"
                    optionKey="business_name"
                    backgroundColor="var(--bg-paper)"
                    apiEndPoint={(limit, skip, search) =>
                      clientService.fetch(limit, skip, search, null, "Active", [
                        "business_name",
                      ])
                    }
                  />
                </Box>
              )}
            </Paper>

            <Divider />

            {/* Row input */}
            <Typography variant="subtitle2" fontWeight={600}>
              {period.dimension === "product_type"
                ? "Add Product Type Entry"
                : "Add Category Entry"}
            </Typography>
            {period.dimension === "product_type" ? (
              <TextField
                label="Product Type *"
                value={rowData.product_type}
                onChange={(e) =>
                  setRowData((p) => ({ ...p, product_type: e.target.value }))
                }
                size="small"
                fullWidth
              />
            ) : (
              <AutocompleteWithPagination
                customLabel="Category *"
                selectedClient={clientId ? { _id: clientId } : period.client}
                backgroundColor="var(--bg-paper)"
                selectedItem={rowData.category}
                onDataChange={(v) =>
                  setRowData((p) => ({ ...p, category: v || null }))
                }
                width="100%"
                optionKey="name"
                apiEndPoint={(limit, skip, search) =>
                  categoryService.fetchNames(
                    search,
                    clientId || period.client?._id,
                    limit,
                    skip,
                  )
                }
              />
            )}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Unit Sold *"
                name="unit_sold"
                type="number"
                value={rowData.unit_sold}
                onChange={handleRowChange}
                inputProps={{ step: "1", min: "0" }}
                size="small"
                fullWidth
              />
              <CurrencyTextField
                label="Total Revenue *"
                name="total_revenue"
                value={rowData.total_revenue}
                onChange={(value) =>
                  setRowData((p) => ({
                    ...p,
                    total_revenue: value === "" ? "" : parseFloat(value),
                  }))
                }
                size="small"
                fullWidth
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="DropShip Units"
                name="dropship_units"
                type="number"
                value={rowData.dropship_units}
                onChange={handleRowChange}
                inputProps={{ step: "1", min: "0" }}
                size="small"
                fullWidth
              />
              <CurrencyTextField
                label="DropShip Revenue"
                name="dropship_revenue"
                value={rowData.dropship_revenue}
                onChange={(value) =>
                  setRowData((p) => ({
                    ...p,
                    dropship_revenue: value === "" ? "" : parseFloat(value),
                  }))
                }
                size="small"
                fullWidth
              />
            </Box>
            <Button
              onClick={handleAddRow}
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={loading}
              fullWidth
            >
              Add to Batch
            </Button>

            {/* Batch entries list */}
            {entries.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Queued Entries ({entries.length})
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <List disablePadding sx={{ maxHeight: 220, overflow: "auto" }}>
                  {entries.map((entry) => (
                    <ListItem
                      key={entry.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleRemoveEntry(entry.id)}
                          disabled={loading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                      sx={{
                        bgcolor: "var(--row-bg-even)",
                        mb: 0.5,
                        borderRadius: 1,
                        px: 1.5,
                      }}
                    >
                      <ListItemText
                        primary={
                          entry.category?.name || entry.product_type || "—"
                        }
                        secondary={`${entry.unit_sold} units • $${parseFloat(entry.total_revenue).toFixed(2)}${entry.dropship_revenue > 0 ? ` • DS: $${parseFloat(entry.dropship_revenue).toFixed(2)}` : ""}`}
                        primaryTypographyProps={{
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                        secondaryTypographyProps={{ fontSize: 12 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>

        {!selectedEntry && entries.length > 0 && (
          <Typography
            variant="caption"
            sx={{ mr: "auto", color: "text.secondary" }}
          >
            {entries.length} {entries.length === 1 ? "entry" : "entries"} ready
          </Typography>
        )}

        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || (!selectedEntry && entries.length === 0)}
          sx={{ position: "relative", minWidth: 100 }}
        >
          {loading && (
            <CircularProgress size={20} sx={{ position: "absolute" }} />
          )}
          <span style={{ visibility: loading ? "hidden" : "visible" }}>
            {selectedEntry
              ? "Update"
              : `Create${entries.length > 0 ? ` (${entries.length})` : ""}`}
          </span>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalesByCategoryModal;
